import React from 'react';
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight, Check } from 'lucide-react';

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

export default function Contact() {
  const [form, setForm] = React.useState({
    name: '',
    email: '',
    company: '',
    interest: 'Cross-Platform Development',
    message: '',
    botcheck: '',
  });
  const [status, setStatus] = React.useState({ state: 'idle', msg: '' }); // idle | submitting | success | error
  const [touched, setTouched] = React.useState({});

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const markTouched = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const errors = {
    name: form.name.trim().length < 2 ? 'Please enter your name.' : null,
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? 'Please enter a valid email.' : null,
    message: form.message.trim().length < 10 ? 'Tell us a bit more (at least 10 characters).' : null,
  };
  const isValid = !errors.name && !errors.email && !errors.message;

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (!isValid) return;

    setStatus({ state: 'submitting', msg: '' });
    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `New enquiry — ${form.interest} — ${form.name}`,
        from_name: 'Veventex Website',
        name: form.name,
        email: form.email,
        company: form.company,
        interest: form.interest,
        message: form.message,
        botcheck: form.botcheck,
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ state: 'success', msg: 'Thanks. We\'ll be in touch within one business day.' });
        setForm({ name: '', email: '', company: '', interest: 'Cross-Platform Development', message: '', botcheck: '' });
        setTouched({});
      } else {
        setStatus({ state: 'error', msg: data.message || 'Something went wrong. Please email us directly.' });
      }
    } catch (err) {
      setStatus({ state: 'error', msg: 'Network error. Please email us directly.' });
    }
  };

  const inputClass =
    'w-full bg-transparent border-0 border-b border-black/20 focus:border-ink focus:outline-none py-3 text-ink placeholder:text-ink-muted/60 transition-colors';

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
          Contact
        </div>
        <h1 className="font-display font-bold tracking-tighter text-5xl md:text-7xl text-ink max-w-4xl leading-[0.95]">
          Tell us about<br />the problem.
        </h1>
        <p className="mt-6 text-lg text-ink-muted max-w-2xl leading-relaxed">
          The more honest you are about constraints — budget, timeline, internal
          politics — the more useful our first reply will be.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 border border-black/10">
          {/* CONTACT INFO */}
          <aside className="lg:col-span-4 bg-cream-alt p-10 lg:p-12">
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-6">
              Direct lines
            </div>

            <div className="space-y-8">
              <a
                href="mailto:info@veventex.com.au"
                data-testid="contact-email-link"
                className="group block"
              >
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-2">
                  <Mail size={12} strokeWidth={1.5} /> Email
                </div>
                <div className="font-display text-2xl md:text-3xl text-ink group-hover:text-ember transition-colors break-all">
                  info@veventex.com.au
                </div>
              </a>

              <a
                href="tel:+61414490141"
                data-testid="contact-phone-link"
                className="group block"
              >
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-2">
                  <Phone size={12} strokeWidth={1.5} /> Phone
                </div>
                <div className="font-display text-2xl md:text-3xl text-ink group-hover:text-ember transition-colors">
                  +61 414 490 141
                </div>
              </a>

              <div>
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-2">
                  <MapPin size={12} strokeWidth={1.5} /> Studio
                </div>
                <div className="font-display text-xl text-ink">Sydney, Australia</div>
                <div className="text-sm text-ink-muted mt-1">By appointment</div>
              </div>

              <a
                href="https://www.linkedin.com/company/veventex"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="group inline-flex items-center gap-2 border-b border-ink pb-1 text-sm font-medium hover:text-ember hover:border-ember transition-colors"
              >
                <Linkedin size={14} strokeWidth={1.5} /> LinkedIn
                <ArrowUpRight size={14} />
              </a>
            </div>

            <div className="mt-12 pt-8 border-t border-black/10">
              <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-3">
                Response time
              </div>
              <div className="text-ink leading-relaxed">
                One business day for new enquiries. If it&apos;s urgent, mention that in
                your message — we triage on import.
              </div>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-8 p-10 lg:p-12">
            {status.state === 'success' ? (
              <div data-testid="contact-form-success" className="py-12">
                <div className="inline-flex items-center justify-center h-12 w-12 bg-ink text-cream mb-6">
                  <Check size={20} />
                </div>
                <h2 className="font-display font-bold tracking-tighter text-3xl md:text-4xl text-ink mb-3">
                  Message received.
                </h2>
                <p className="text-ink-muted max-w-md leading-relaxed">{status.msg}</p>
                <button
                  onClick={() => setStatus({ state: 'idle', msg: '' })}
                  data-testid="contact-form-reset"
                  className="mt-8 inline-flex items-center gap-2 border border-ink/20 px-5 py-2.5 text-sm font-medium hover:border-ink transition-colors"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
                {/* honeypot */}
                <input
                  type="checkbox"
                  name="botcheck"
                  className="hidden"
                  tabIndex="-1"
                  autoComplete="off"
                  value={form.botcheck}
                  onChange={setField('botcheck')}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={setField('name')}
                      onBlur={markTouched('name')}
                      data-testid="contact-input-name"
                      className={inputClass}
                      placeholder="Your full name"
                    />
                    {touched.name && errors.name && (
                      <div data-testid="contact-error-name" className="text-xs text-red-700 mt-1">{errors.name}</div>
                    )}
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={setField('email')}
                      onBlur={markTouched('email')}
                      data-testid="contact-input-email"
                      className={inputClass}
                      placeholder="you@company.com"
                    />
                    {touched.email && errors.email && (
                      <div data-testid="contact-error-email" className="text-xs text-red-700 mt-1">{errors.email}</div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
                      Company
                    </label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={setField('company')}
                      data-testid="contact-input-company"
                      className={inputClass}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
                      Interested in
                    </label>
                    <select
                      value={form.interest}
                      onChange={setField('interest')}
                      data-testid="contact-input-interest"
                      className={`${inputClass} appearance-none cursor-pointer`}
                    >
                      <option>Cross-Platform Development</option>
                      <option>AI Integration</option>
                      <option>AI Security</option>
                      <option>AI Training</option>
                      <option>General enquiry</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
                    Project details *
                  </label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={setField('message')}
                    onBlur={markTouched('message')}
                    data-testid="contact-input-message"
                    className={`${inputClass} resize-none`}
                    placeholder="What are you building, when do you need it, and what does success look like?"
                  />
                  {touched.message && errors.message && (
                    <div data-testid="contact-error-message" className="text-xs text-red-700 mt-1">{errors.message}</div>
                  )}
                </div>

                {status.state === 'error' && (
                  <div data-testid="contact-form-error" className="border border-red-700/30 bg-red-50 text-red-800 p-4 text-sm">
                    {status.msg}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                  <div className="text-xs text-ink-muted font-mono tracking-tight">
                    We reply within one business day.
                  </div>
                  <button
                    type="submit"
                    disabled={status.state === 'submitting'}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm font-medium hover:bg-ember transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status.state === 'submitting' ? 'Sending…' : 'Send enquiry'}
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
